import {useEffect, useRef} from 'react';
import {useDispatch} from '@utils';
import {boardSlice} from '@reducers';
import {useLazyFetchAnalysisQuery} from '@api';
import {TNode, Analysis, sgfToA1} from 'ghostban';

const getNodePath = (node: TNode | null): string => {
    if (!node) return '';

    const pathNodes = node.getPath();
    return pathNodes
        .map((n: TNode) => {
            const moveProps = n.model?.moveProps?.[0];
            return moveProps ? sgfToA1(moveProps.toString()) : '';
        })
        .filter((move: string) => move)
        .join(',');
};

type UseLoadNodeAnalysisParams = {
    currentNode: TNode | null | undefined;
    scenarioId: number | undefined;
    difficulty: string | undefined;
    showAnalysis: boolean;
    showOwnership?: boolean;
    refreshCounter?: number;
};

type UseLoadNodeAnalysisResult = {
    analysis: Analysis | undefined;
};

const analysisCache = new Map<string, Analysis>();

const loadAnalysisFromUrl = async (analysisUrl: string): Promise<Analysis> => {
    const response = await fetch(analysisUrl);
    if (!response.ok) {
        throw new Error(`Failed to load analysis from ${analysisUrl}`);
    }

    return JSON.parse(await response.text());
};

const normalizeAnalysis = (analysisData: Analysis): Analysis => {
    if (typeof analysisData.id === 'string' || !analysisData.id) {
        analysisData.id = JSON.stringify({bx: 19, by: 19});
    }

    return analysisData;
};

export const useLoadNodeAnalysis = ({
    currentNode,
    scenarioId,
    difficulty,
    showAnalysis,
    showOwnership = false,
    refreshCounter = 0,
}: UseLoadNodeAnalysisParams): UseLoadNodeAnalysisResult => {
    const dispatch = useDispatch();
    const [fetchAnalysis] = useLazyFetchAnalysisQuery();
    const loadingPaths = useRef<Set<string>>(new Set());
    const lastRefreshCounter = useRef(refreshCounter);

    const path = currentNode ? getNodePath(currentNode) : '';
    const cacheKey =
        scenarioId && difficulty ? `${scenarioId}:${difficulty}:${path}` : '';

    // Get cached analysis for current path
    const cachedAnalysis = cacheKey ? analysisCache.get(cacheKey) : undefined;

    useEffect(() => {
        if (
            (!showAnalysis && !showOwnership) ||
            !currentNode ||
            !scenarioId ||
            !difficulty
        ) {
            return;
        }

        const forceRefresh = lastRefreshCounter.current !== refreshCounter;

        if (cachedAnalysis && !forceRefresh) {
            return;
        }

        if (loadingPaths.current.has(cacheKey)) {
            return;
        }

        loadingPaths.current.add(cacheKey);

        fetchAnalysis({scenarioId, path, difficulty})
            .unwrap()
            .then(async result => {
                try {
                    let analysisData: Analysis | undefined;
                    if (
                        result.analysis !== null &&
                        result.analysis !== undefined
                    ) {
                        analysisData = JSON.parse(result.analysis);
                    } else if (result.analysisUrl) {
                        analysisData = await loadAnalysisFromUrl(
                            result.analysisUrl
                        );
                    }

                    if (analysisData) {
                        analysisCache.set(
                            cacheKey,
                            normalizeAnalysis(analysisData)
                        );

                        dispatch(boardSlice.actions.forceUpdate());
                    }
                } catch {
                    // Failed to parse analysis
                    console.log(
                        'Failed to load analysis data:',
                        result.analysisUrl
                    );
                }
            })
            .catch(() => {
                // Remove from loading set so it can be retried
                loadingPaths.current.delete(cacheKey);
            })
            .finally(() => {
                lastRefreshCounter.current = refreshCounter;
                loadingPaths.current.delete(cacheKey);
            });
    }, [
        showAnalysis,
        showOwnership,
        currentNode,
        scenarioId,
        difficulty,
        path,
        cacheKey,
        cachedAnalysis,
        refreshCounter,
        fetchAnalysis,
        dispatch,
    ]);

    return {analysis: cachedAnalysis};
};
